import React from 'react';
import { Briefcase, Receipt, HeartPulse, Sunrise } from 'lucide-react';
import { siteData } from '../data/mock';

const iconMap = {
  Briefcase: Briefcase,
  Receipt: Receipt,
  HeartPulse: HeartPulse,
  Sunrise: Sunrise
};

const WhyItMatters = () => {
  return (
    <section id="why-it-matters" className="py-24 bg-stone-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-emerald-600 font-semibold text-sm uppercase tracking-wider mb-4">
            Understanding The Crisis
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-stone-800 mb-6">
            Why This Matters
          </h2>
          <p className="text-lg text-stone-600 max-w-3xl mx-auto leading-relaxed">
            From 2010 to 2030, hardworking Jamaicans — especially those in the Business Process 
            Outsourcing (BPO) sector — have faced ongoing economic hardship, stagnating wages, 
            and disproportionate tax burdens.
          </p>
        </div>

        {/* Issues Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {siteData.issues.map((issue, index) => {
            const IconComponent = iconMap[issue.icon];
            const isHighlighted = index === 3;
            
            return (
              <div
                key={issue.id}
                className={`group relative p-8 rounded-2xl transition-all duration-300 hover:-translate-y-1 ${
                  isHighlighted
                    ? 'bg-gradient-to-br from-emerald-600 to-emerald-700 text-white shadow-xl shadow-emerald-600/20'
                    : 'bg-white border border-stone-200 hover:border-emerald-200 hover:shadow-lg'
                }`}
              >
                {/* Icon */}
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 transition-transform group-hover:scale-110 ${
                  isHighlighted
                    ? 'bg-white/20'
                    : 'bg-emerald-50 group-hover:bg-emerald-100'
                }`}>
                  <IconComponent className={`w-7 h-7 ${
                    isHighlighted ? 'text-white' : 'text-emerald-600'
                  }`} />
                </div>

                {/* Content */}
                <h3 className={`text-xl font-bold mb-3 ${
                  isHighlighted ? 'text-white' : 'text-stone-800'
                }`}>
                  {issue.title}
                </h3>
                <p className={`leading-relaxed ${
                  isHighlighted ? 'text-white/90' : 'text-stone-600'
                }`}>
                  {issue.description}
                </p>

                {/* Decorative Element */}
                {isHighlighted && (
                  <div className="absolute top-4 right-4 w-24 h-24 bg-white/10 rounded-full blur-2xl" />
                )}
              </div>
            );
          })}
        </div>

        {/* Image Section */}
        <div className="mt-16 grid md:grid-cols-2 gap-8 items-center">
          <div className="relative">
            <img
              src={siteData.images.workers}
              alt="Jamaican workers"
              className="rounded-2xl shadow-xl w-full h-80 object-cover"
            />
            <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-amber-400 rounded-2xl -z-10" />
          </div>
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-stone-800">
              The Reality of Jamaican Workers
            </h3>
            <p className="text-stone-600 leading-relaxed">
              Despite being on the frontlines of a growing global industry, many Jamaican 
              employees continue to receive below-livable wages while shouldering high taxes, 
              inadequate healthcare access, and limited worker protections.
            </p>
            <p className="text-stone-600 leading-relaxed">
              This imbalance has hurt families, stunted economic mobility, and contributed to 
              stress-related health challenges across communities.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyItMatters;
